import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/Button";
import { formatPlural } from "@/shared/utils/formatter.utils";
import { JOB_RESOURCE } from "../../configs/constants";
import { useCancelJob, useCancelEmployeeImport } from "../../hooks/useJobsMutations";
import type { Job } from "../../types/jobs.types";

type JobCancelConfirmationProps = {
  job: Job
  onCancelled: () => void
};

// Grace period before the confirm button becomes clickable, so a stray double-click
// on the trigger cannot immediately confirm the irreversible cancellation.
export const CONFIRM_ENABLE_DELAY_MS = 1000;

const CancelConfirmationContent = ({ job }: { job: Job }) => {
  const preview = job.import_cancellation_preview;

  if (!preview) {
    return <>Cette action annulera la tâche en cours. Cette opération est irréversible.</>;
  }

  const { deletable_count: deletableCount, retained_count: retainedCount } = preview;

  return (
    <>
      <strong>
        {deletableCount}
        {" "}
        {formatPlural(deletableCount, "salarié")}
        {" "}
        {formatPlural(deletableCount, "créé")}
      </strong>
      {" "}
      {deletableCount > 1 ? "seront supprimés" : "sera supprimé"}
      {retainedCount > 0 && (
        <>
          ,
          {" "}
          <strong>
            {retainedCount}
            {" "}
            {formatPlural(retainedCount, "conservé")}
          </strong>
          {" "}
          (
          {formatPlural(retainedCount, "déjà connecté")}
          )
        </>
      )}
      .
    </>
  );
};

const JobCancelConfirmation = ({ job, onCancelled }: JobCancelConfirmationProps) => {
  // Employee imports cancel asynchronously (reverse-progress); every other type is deleted
  // synchronously by the legacy path. The endpoint/UX differ, so route on the job resource.
  const isEmployeeImport = job.resource === JOB_RESOURCE.EMPLOYEE_IMPORT;
  const cancelJobMutation = useCancelJob();
  const cancelEmployeeImportMutation = useCancelEmployeeImport();
  const mutation = isEmployeeImport ? cancelEmployeeImportMutation : cancelJobMutation;
  const [isConfirming, setIsConfirming] = useState(false);
  const [isConfirmEnabled, setIsConfirmEnabled] = useState(false);

  useEffect(() => {
    if (!isConfirming) return;
    const timer = setTimeout(() => setIsConfirmEnabled(true), CONFIRM_ENABLE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isConfirming]);

  const enterConfirmation = () => {
    setIsConfirmEnabled(false);
    setIsConfirming(true);
  };

  const exitConfirmation = () => {
    setIsConfirming(false);
    setIsConfirmEnabled(false);
  };

  const handleCancelJob = async () => {
    try {
      await mutation.mutateAsync(job.uuid);
      if (isEmployeeImport) {
        // Keep the modal open on the same job: the status flips to cancelling (via Pusher) and the
        // modal switches to the descending-gauge view. Just collapse the confirmation panel.
        exitConfirmation();
      }
      else {
        onCancelled();
      }
    }
    catch {
      // The mutation's onError callback already handles the error (toast via meta.errorMessage).
      // We stay in the confirmation state so the user can retry or go back.
    }
  };

  const confirmLabel = mutation.isPending ? "Annulation..." : "Confirmer l'annulation";

  return (
    <div className="flex flex-1 flex-col gap-3">
      {isConfirming && (
        <div className="flex items-center gap-3 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-destructive animate-in fade-in slide-in-from-top-1 duration-300">
          <AlertTriangle className="size-7 shrink-0" aria-hidden />
          <p className="text-sm font-medium">
            <CancelConfirmationContent job={job} />
          </p>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          aria-hidden={!isConfirming}
          tabIndex={isConfirming ? 0 : -1}
          className={cn(
            "overflow-hidden whitespace-nowrap transition-all duration-300 ease-out",
            isConfirming ? "flex-1 opacity-100" : "pointer-events-none w-0 min-w-0 flex-none border-0 p-0 opacity-0",
          )}
          onClick={exitConfirmation}
          disabled={mutation.isPending}
        >
          Retour
        </Button>

        <Button
          variant="destructive"
          className={cn(
            "whitespace-nowrap transition-all duration-300 ease-out",
            isConfirming ? "flex-1" : "flex-none",
          )}
          onClick={isConfirming ? handleCancelJob : enterConfirmation}
          disabled={isConfirming && (!isConfirmEnabled || mutation.isPending)}
        >
          {isConfirming ? confirmLabel : "Annuler cette tâche"}
        </Button>
      </div>
    </div>
  );
};

export default JobCancelConfirmation;
